import { configureStore } from '@reduxjs/toolkit';
import type { ThunkAction } from '@reduxjs/toolkit';
import type { PublicRuntimeConfig } from 'app.types';
import { reducer as newPromoSpecial } from 'domains/pages/promo-special-new/promo-special-new.reducer';
import { reducer as specialOfferPage } from 'domains/pages/special-offer';
import { reducer as webSpecialOfferPage } from 'domains/pages/web-special-offer';
import { reducer as androidSpecialPage } from 'domains/pages/android-special';
import { reducer as mobilePayment } from 'domains/payments/mobile';
import { reducer as paymentsProcessing } from 'domains/payments/processing';
import { reducer as productsV3 } from 'domains/products';
// import LogRocket from 'logrocket';
import { createWrapper } from 'next-redux-wrapper';
import getConfig from 'next/config';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import type { Action } from 'redux';

import articlesReducer from 'redux-store/slices/articles';
import experimentsReducer from 'redux-store/slices/experiments';
import { JwtSlice } from 'redux-store/slices/jwt';
import landingsReducer from 'redux-store/slices/landings';
import LMSReducer from 'redux-store/slices/lms';
import { NotificationsSlice } from 'redux-store/slices/notifications';
import PaymentsMethodsReducer from 'redux-store/slices/paymentsMethods';
import ProductsReducer from 'redux-store/slices/products';
import profileReducer from 'redux-store/slices/profile';
import PurchasesReducer from 'redux-store/slices/purchases';
import uiReducer from 'redux-store/slices/ui';
import onboardingReducer from 'redux-store/slices/survey';

const reducer = {
  profile: profileReducer,
  ui: uiReducer,
  lms: LMSReducer,
  products: ProductsReducer,
  purchases: PurchasesReducer,
  paymentsMethods: PaymentsMethodsReducer,
  experiments: experimentsReducer,
  articles: articlesReducer,
  landings: landingsReducer,
  jwt: JwtSlice.reducer,
  notifications: NotificationsSlice.reducer,
  mobilePayment,
  productsV3,
  specialOfferPage,
  webSpecialOfferPage,
  androidSpecialPage,
  paymentsProcessing,
  newPromoSpecial,
  onboarding: onboardingReducer
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeStore = () => {
  const { publicRuntimeConfig } = getConfig();
  const { app }: PublicRuntimeConfig = publicRuntimeConfig;
  const { isDev } = app.env;

  return configureStore({
    reducer,
    devTools: isDev,
  });
};

export type AppStore = ReturnType<typeof makeStore>;
export type AppGetState = AppStore['getState'];
export type AppState = ReturnType<AppGetState>;
export type AppThunk<ReturnType> = ThunkAction<
  ReturnType,
  AppState,
  unknown,
  Action
>;

export interface HydrateAction<T> extends Action {
  payload: {
    [stateName: string]: T;
  };
}

export const wrapper = createWrapper<AppStore>(makeStore, {
  debug: false,
});

export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<AppState> = useSelector;
